import { useEffect, useState } from 'react';
import { Medal } from '../Achievements';
import { Modal } from '../../components/Modal';
import { api, json } from '../../api';
import { UnifiedSelect } from '../../components/UnifiedSelect';
const actions: Record<string, string> = {
  game: '游戏上报',
  platform: '平台自动',
  legacy: '历史记录',
  grant: '管理员补发',
  revoke: '撤销',
  restore: '恢复',
  reset: '重新开放挑战',
};
type RecordItem = {
  game_id: string;
  id: string;
  game_name: string;
  definition: {
    name: string;
    icon?: number;
    target: number;
    points: number;
    badge: string;
    title: string;
    status?: string;
  };
  value: number;
  points: number;
  unlocked_at: string | null;
  test: number;
  blocked: number;
  revision: number;
  source: string;
};
type Data = {
  user: { id: string; nickname: string; username: string };
  items: RecordItem[];
  points: number;
  equipment: { badges: string; title: string };
  history: {
    seq: number;
    action: string;
    actor: string;
    reason: string;
    note: string;
    delta: number;
    created_at: string;
    before_state: string;
    after_state: string;
  }[];
  historyTotal: number;
};
export function AchievementRecords({ userId }: { userId?: string }) {
  const [id, setId] = useState(userId || ''),
    [query, setQuery] = useState(''),
    [users, setUsers] = useState<{ id: string; nickname: string; username: string }[]>([]),
    [data, setData] = useState<Data | null>(null),
    [error, setError] = useState(''),
    [page, setPage] = useState(1),
    [busy, setBusy] = useState(false),
    [selected, setSelected] = useState<RecordItem | null>(null),
    [action, setAction] = useState('grant'),
    [reason, setReason] = useState(''),
    [note, setNote] = useState(''),
    [requestId, setRequestId] = useState(''),
    [submitted, setSubmitted] = useState(false),
    [filter, setFilter] = useState('');
  const load = () =>
    api<Data>('/admin/achievements/users/' + encodeURIComponent(id) + '?page=' + page).then(
      setData,
    );
  useEffect(() => {
    setData(null);
    if (!id) return;
    let active = true;
    api<Data>('/admin/achievements/users/' + encodeURIComponent(id) + '?page=' + page)
      .then((r) => {
        if (active) setData(r);
      })
      .catch((e) => {
        if (active) setError(e.message);
      });
    return () => {
      active = false;
    };
  }, [id, page]);
  useEffect(() => {
    if (userId) setId(userId);
  }, [userId]);
  async function search() {
    setError('');
    try {
      setUsers(
        (
          await api<{ items: typeof users }>(
            '/admin/achievements/users/search?q=' + encodeURIComponent(query),
          )
        ).items,
      );
    } catch (e) {
      setError((e as Error).message);
    }
  }
  function choose(i: RecordItem, a: string) {
    setSelected(i);
    setAction(a);
    setReason('');
    setNote('');
    setRequestId(crypto.randomUUID());
    setSubmitted(false);
    setError('');
  }
  async function submit() {
    if (!selected || busy) return;
    setBusy(true);
    setSubmitted(true);
    setError('');
    try {
      await api(
        '/admin/achievements/users/' + encodeURIComponent(id) + '/actions',
        json('POST', {
          requestId,
          gameId: selected.game_id,
          id: selected.id,
          action,
          reason,
          note,
          expectedRevision: selected.revision,
        }),
      );
      setSelected(null);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="achievement-records">
      {!userId && (
        <div className="achievement-editor-tools">
          <input
            aria-label="搜索玩家"
            placeholder="昵称、账号或用户 ID"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button className="button secondary" onClick={() => void search()}>
            查找玩家
          </button>
          <UnifiedSelect
            value={id}
            onChange={(e) => {
              setId(e.target.value);
              setPage(1);
              setSelected(null);
            }}
          >
            <option value="">选择玩家</option>
            {users.map((u) => (
              <option value={u.id} key={u.id}>
                {u.nickname || u.username} · {u.username}
              </option>
            ))}
          </UnifiedSelect>
        </div>
      )}
      {error && (
        <p role="alert" className="ach-error">
          {error}
        </p>
      )}
      {!data && !error && (
        <div className="achievement-empty">
          <strong>{id ? '正在加载玩家记录…' : '查找玩家，查看成就与荣誉'}</strong>
        </div>
      )}
      {data && (
        <>
          <div className="achievement-editor-tools">
            <div className="achievement-player-identity">
              <span className="achievement-avatar">
                {(data.user.nickname || data.user.username).slice(0, 1)}
              </span>
              <div>
                <strong>{data.user.nickname || data.user.username}</strong>
                <small>@{data.user.username}</small>
              </div>
            </div>
            <span>
              {data.points} 积分 · Lv.{1 + Math.floor(data.points / 200)}
            </span>
            <button
              className="button secondary small"
              disabled={busy}
              onClick={() => void load().catch((e) => setError(e.message))}
            >
              刷新记录
            </button>
            <input
              aria-label="筛选成就"
              placeholder="搜索游戏或成就"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          <div className="achievement-player-metrics">
            <div>
              <small>成就积分</small>
              <strong>{data.points}</strong>
            </div>
            <div>
              <small>已获得成就</small>
              <strong>{data.items.filter((i) => i.unlocked_at && !i.blocked).length}</strong>
            </div>
            <div>
              <small>已撤销</small>
              <strong>{data.items.filter((i) => i.blocked).length}</strong>
            </div>
          </div>
          <p className="achievement-equipment">
            佩戴徽章：
            {(JSON.parse(data.equipment.badges) as string[])
              .map(
                (k) => data.items.find((i) => i.game_id + ':' + i.id === k)?.definition.badge || k,
              )
              .join('、') || '无'}
            　称号：
            {data.items.find((i) => i.game_id + ':' + i.id === data.equipment.title)?.definition
              .title || '无'}
          </p>
          {selected && (
            <Modal title={actions[action]} onClose={() => setSelected(null)} dismissible={!busy} className="achievement-action-dialog">
              <form
                className="achievement-action"
                onSubmit={(e) => {
                  e.preventDefault();
                  void submit();
                }}
              >
                <h3>
                  {actions[action]} · {selected.definition.name}
                </h3>
                <p>
                  {action === 'grant'
                    ? `将增加 ${selected.definition.points} 积分${selected.definition.badge ? '，授予徽章「' + selected.definition.badge + '」' : ''}${selected.definition.title ? '，授予称号「' + selected.definition.title + '」' : ''}`
                    : action === 'revoke'
                      ? `将扣回 ${selected.points} 积分；无其他获得来源的徽章和称号将收回并取消佩戴。此成就将暂停授予。`
                      : action === 'restore'
                        ? '恢复原授予时间、积分及荣誉，解除暂停授予；不会自动更改玩家当前佩戴。'
                        : '清空旧进度并重新开放获取。单机游戏仍可能从旧存档重报达标进度。'}
                </p>
                <label>
                  给玩家的处理原因
                  <textarea
                    required
                    minLength={2}
                    maxLength={300}
                    value={reason}
                    disabled={submitted}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </label>
                <label>
                  内部备注（不展示给玩家）
                  <textarea
                    maxLength={1000}
                    value={note}
                    disabled={submitted}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </label>
                <div className="achievement-editor-tools">
                  <button className="button" disabled={busy || reason.trim().length < 2}>
                    {busy ? '处理中…' : submitted ? '重试同一操作' : '确认' + actions[action]}
                  </button>
                  <button
                    type="button"
                    className="button secondary"
                    disabled={busy}
                    onClick={() => setSelected(null)}
                  >
                    取消
                  </button>
                </div>
                {error && (
                  <p className="ach-error" role="alert">
                    {error}
                  </p>
                )}
              </form>
            </Modal>
          )}
          <div className="achievement-record-list">
            {data.items
              .filter(
                (i) =>
                  (i.game_name || '平台成就').includes(filter) ||
                  i.definition.name.includes(filter),
              )
              .map((i) => (
                <article key={i.game_id + ':' + i.id}>
                  <Medal icon={i.definition.icon || 0} />
                  <div className="achievement-record-description">
                    <strong>{i.definition.name}</strong>
                    <small>
                      {i.game_name || '平台成就'} ·{' '}
                      {i.blocked
                        ? '已撤销 / 暂停授予'
                        : i.unlocked_at
                          ? '已获得'
                          : `${i.value || 0} / ${i.definition.target}`}{' '}
                      · {i.test ? '测试' : '正式'}
                    </small>
                    <small>
                      {actions[i.source] || '未授予'}
                      {i.unlocked_at ? ' · ' + new Date(i.unlocked_at).toLocaleString() : ''}
                    </small>
                  </div>
                  <span>{i.points || 0} 分</span>
                  <div className="achievement-record-actions">
                    {i.blocked ? (
                      <>
                        <button disabled={busy} onClick={() => choose(i, 'restore')}>
                          恢复
                        </button>
                        <button disabled={busy} onClick={() => choose(i, 'reset')}>
                          重新挑战
                        </button>
                      </>
                    ) : i.unlocked_at ? (
                      <button disabled={busy} onClick={() => choose(i, 'revoke')}>
                        撤销
                      </button>
                    ) : (
                      <button
                        disabled={
                          busy || (!!i.definition.status && i.definition.status !== 'active')
                        }
                        onClick={() => choose(i, 'grant')}
                      >
                        补发
                      </button>
                    )}
                  </div>
                </article>
              ))}
          </div>
          <h3>处理历史</h3>
          <div className="achievement-history">
            {data.history.map((h) => (
              <details key={h.seq}>
                <summary>
                  {actions[h.action] || h.action} · {h.delta > 0 ? '+' : ''}
                  {h.delta} 分 · {new Date(h.created_at).toLocaleString()}
                </summary>
                <p>{h.reason || '自动授予'}</p>
                {h.actor && <p>操作人：{h.actor}</p>}
                {h.note && <p>内部备注：{h.note}</p>}
                <pre>
                  操作前：{h.before_state}
                  {'\n'}操作后：{h.after_state}
                </pre>
              </details>
            ))}
            {!data.history.length && <p>暂无处理记录</p>}
          </div>
          <div className="achievement-editor-tools">
            <button
              className="button secondary small"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              上一页
            </button>
            <span>
              第 {page} 页 · {data.historyTotal} 条
            </span>
            <button
              className="button secondary small"
              disabled={page * 30 >= data.historyTotal}
              onClick={() => setPage(page + 1)}
            >
              下一页
            </button>
          </div>
        </>
      )}
    </section>
  );
}
